<!--
  PlayerPage · 播放器页（路由 /lessons/:id/play）
  - 从路由参数取 lesson id，加载元数据 + 句子 + 同包课程列表
  - 顶部 HeaderBar（字幕/模式/倍速/预设快切）
  - 主体：左侧 VideoCard + 下方 SentenceList；右侧 同包课程列表（学完一集直接点下一集）
  - 全局快捷键（空格/←/→/R/L/A/[/]/1-4）
-->
<template>
  <div class="player-page">
    <HeaderBar
      :sub-mode="state.subMode"
      :adaptive="state.adaptive"
      :lock-one="state.lock_one"
      :current-rate="currentRate"
      :speeds="SPEED_OPTIONS"
      :presets="state.presets"
      :active-preset-id="state.activePresetId"
      :lesson-title="state.lesson?.title || ''"
      @sub-click="loop && loop.applySubtitleMode($event)"
      @toggle-adaptive="onToggleAdaptive"
      @toggle-lock="onToggleLock"
      @rate-click="onRateClick"
      @change-preset="onChangePreset"
      @back="onBack"
    />

    <main class="app-main">
      <!-- 左侧：视频 + 句子列表（垂直堆叠） -->
      <div class="left-col">
        <VideoCard
          ref="videoCardRef"
          :chip-sent="chipSent"
          :chip-step="chipStep"
          :chip-step-on="chipStepOn"
          :chip-lock="chipLock"
          :chip-lock-on="chipLockOn"
          @click-prev="loop && loop.gotoPrevSentence()"
          @click-replay="loop && loop.seekSentence(state.si)"
          @click-next="onClickNext"
        />
        <!-- 句子列表：视频下方，固定约 5 句高度，可滚动 -->
        <div class="sen-panel-wrap">
          <SentenceList
            ref="listRef"
            :sentences="state.sentences"
            :current-idx="state.si"
            @select="onSelectSentence"
            @select-text="onSelectText"
          />
        </div>
      </div>

      <!-- 右侧：同课程包的其他课程（学完一集直接点下一集） -->
      <aside class="lessons-col">
        <div class="col-head">
          <b>📚 本课程包</b>
          <span class="chip">{{ packLessons.length }} 集</span>
        </div>
        <div class="pack-lessons-list">
          <div v-if="!packLessons.length" class="empty-hint">加载中…</div>
          <div
            v-for="(l, i) in packLessons"
            :key="l.id"
            class="pack-lesson-item"
            :class="{
              current: l.id === state.lesson?.id,
              disabled: (l.upload_step || 0) < 4,
            }"
            @click="onSwitchLesson(l)"
            :title="(l.upload_step || 0) < 4 ? '处理中，暂不能播放' : (l.id === state.lesson?.id ? '当前正在播放' : '点击切换到这一集')"
          >
            <div class="pli-idx">
              <span v-if="l.id === state.lesson?.id" class="playing-mark">▶</span>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <div class="pli-main">
              <div class="pli-title">{{ l.title || '未命名' }}</div>
              <div class="pli-meta">
                <span>{{ l.sentence_count || 0 }} 句</span>
                <span>·</span>
                <span>{{ fmtDur(l.duration_sec || 0) }}</span>
                <span v-if="(l.upload_step || 0) < 4" class="proc-tag">{{ stepText(l.upload_step) }}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </main>

    <div v-if="loadError" class="load-error">
      ❌ {{ loadError }}
      <button @click="onBack">返回课程包</button>
    </div>

    <!-- 选词翻译弹窗 -->
    <TranslatePopup
      :visible="trVisible"
      :text="trText"
      :x="trX"
      :y="trY"
      @close="trVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeaderBar from '@/components/HeaderBar.vue'
import VideoCard from '@/components/VideoCard.vue'
import SentenceList from '@/components/SentenceList.vue'
import TranslatePopup from '@/components/TranslatePopup.vue'
import {
  state, api, toast, applyPreset,
} from '@/composables/usePlayerState'
import { useAdaptiveLoop } from '@/composables/useAdaptiveLoop'

const route = useRoute()
const router = useRouter()

// ============== 常量 ==============
const SPEED_OPTIONS = [0.4, 0.5, 0.75, 0.8, 1.0, 1.2, 1.5, 2.0]

// ============== refs ==============
const videoCardRef = ref(null)
const listRef = ref(null)
const loadError = ref('')

// ============== 同包课程列表（右侧栏，学完一集直接点下一集） ==============
const packLessons = ref([])

// ============== 当前 video + TextTrack ==============
const videoRef = computed(() => videoCardRef.value?.videoRef || null)
const tracksRef = ref({ en: null, zh: null, bi: null })
const currentRate = ref(1.0)

// ============== chip 文本 ==============
const chipSent = ref('句 0 / 0')
const chipStep = ref('顺序播放')
const chipStepOn = ref(false)
const chipLock = ref('🔓 顺序播放')
const chipLockOn = ref(false)

// ============== 选词翻译弹窗 ==============
const trVisible = ref(false)
const trText = ref('')
const trX = ref(0)
const trY = ref(0)

// ============== 播放进度记忆 ==============
let progressTimer = null
let lastSavedPos = 0

async function saveProgress(finished = false) {
  const v = videoRef.value
  if (!v || !state.lesson?.id) return
  const pos = v.currentTime || 0
  // 非结束时，位移不到 2 秒不存（减少无意义的 DB 写入）
  if (!finished && pos > 0 && Math.abs(pos - lastSavedPos) < 2) return
  lastSavedPos = pos
  try {
    await api('/api/lessons/' + state.lesson.id + '/progress', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position: pos, finished }),
    })
  } catch (e) { /* 静默失败，不打扰用户 */ }
}

function onVideoEnded() { saveProgress(true) }
function onVideoPause() { saveProgress(false) }
function onBeforeUnloadProgress() { saveProgress(false) }

// ============== useAdaptiveLoop 实例 ==============
let loop = null

function ensureLoop() {
  if (loop) return loop
  loop = useAdaptiveLoop(videoRef, tracksRef, {
    activeSpeedBtn(rate) {
      currentRate.value = rate
      videoCardRef.value?.applySpeedActive(rate)
    },
    activeSubBtn(/*mode*/) { /* HeaderBar 用 props.subMode 响应式，不需要额外回调 */ },
    markActive(i) { listRef.value?.markActive(i) },
    updateChips({ sent, total, stepTxt, stepOn, lockTxt, lockOn }) {
      chipSent.value = `句 ${sent} / ${total}`
      chipStep.value = stepTxt; chipStepOn.value = stepOn
      chipLock.value = lockTxt; chipLockOn.value = lockOn
    },
  })
  return loop
}

function syncPlaybackRateToPreset() {
  const targetRate = state.staircase[0]?.rate || 1.0
  currentRate.value = targetRate
  videoCardRef.value?.applySpeedActive(targetRate)
  if (loop) loop.applyRate(targetRate)
}

// ============== 数据加载 ==============
async function loadLesson(lessonId) {
  if (!lessonId) return
  loadError.value = ''
  try {
    const [meta, sens] = await Promise.all([
      api('/api/lessons/' + lessonId),
      api('/api/lessons/' + lessonId + '/sentences'),
    ])
    state.lesson = meta
    state.sentences = (sens.items || []).map(s => ({
      idx: s.idx, start: s.start, end: s.end,
      en: s.en || '', zh: s.zh || '', words: s.words,
    }))
    state.si = 0; state.stp = 0; state.rep = 0
    ensureLoop()
    await loop.setupPlayerTextTracks()
    syncPlaybackRateToPreset()
    // 有上次的播放进度 → seek 到该位置；否则从第一句开始
    const savedPos = meta.playback_position || 0
    lastSavedPos = savedPos  // 重置，避免刚进来就触发一次保存
    if (savedPos > 1) {
      const v = videoRef.value
      const doSeek = () => {
        if (v) v.currentTime = savedPos
        toast('📍 已跳转到上次播放位置', 2000)
      }
      if (v && v.readyState >= 1) doSeek()
      else if (v) v.addEventListener('loadedmetadata', doSeek, { once: true })
    } else {
      loop.seekSentence(0, false)
    }
    // 顺手加载同包课程列表（右侧栏；失败不阻塞播放）
    loadPackLessons(meta.pack_id).catch(() => {})
  } catch (e) {
    loadError.value = e.message || '加载失败'
    toast('加载失败：' + e.message, 3000)
  }
}

/** 加载当前课程所在课程包的全部课程（用于右侧"下一集"列表） */
async function loadPackLessons(packId) {
  if (!packId) return
  try {
    const data = await api('/api/course-packs/' + packId + '/lessons')
    packLessons.value = data.items || []
    // 顺手把当前课程的最新状态同步进列表（避免 step 显示旧值）
    if (state.lesson) {
      const cur = packLessons.value.find(l => l.id === state.lesson.id)
      if (cur) cur.upload_step = state.lesson.upload_step
    }
  } catch (e) {
    // 静默失败：右侧列表不是核心功能
    packLessons.value = []
  }
}

/** 点击右侧课程项 → 切换到那一集 */
function onSwitchLesson(l) {
  if (!l || l.id === state.lesson?.id) return
  if ((l.upload_step || 0) < 4) {
    toast('该课程尚未处理完成')
    return
  }
  // 同组件复用：改路由参数，下面的 watch 会触发 loadLesson
  router.push('/lessons/' + l.id + '/play')
}

// ============== 预设 / 模式切换 ==============
function onChangePreset(id) {
  if (!id || id === state.activePresetId) return
  applyPreset(id)
  onPresetApplied()
  const p = state.presets.find(x => x.id === id)
  toast('已切换：' + (p?.name || id))
}
function onPresetApplied() {
  ensureLoop()
  state.stp = 0; state.rep = 0
  syncPlaybackRateToPreset()
}
function onRateClick(r) {
  ensureLoop()
  loop.applyRate(r)
}
function onToggleAdaptive() {
  state.adaptive = !state.adaptive
  state.stp = 0; state.rep = 0
  ensureLoop()
  if (state.adaptive) loop.applyRate(state.staircase[0]?.rate || 1.0)
  toast(state.adaptive ? '🪜 阶梯已开启' : '▶️ 已切换为顺序播放')
}
function onToggleLock() {
  state.lock_one = !state.lock_one
  toast(state.lock_one ? '🔒 锁定当前句循环' : '🔓 解锁')
}
function onClickNext() {
  state.lock_one = false
  ensureLoop()
  loop.gotoNextSentence()
}
function onSelectSentence(i) {
  ensureLoop()
  state.stp = 0; state.rep = 0
  loop.seekSentence(i, true)
}

/** 句子列表选词 → 显示翻译弹窗 */
function onSelectText({ text, x, y }) {
  trText.value = text
  trX.value = x
  trY.value = y
  trVisible.value = true
}

/** 全局点击：关闭翻译弹窗（点击弹窗内部除外） */
function onGlobalClick(e) {
  if (trVisible.value && !e.target.closest('.tr-popup')) {
    trVisible.value = false
  }
}
function onBack() {
  router.push('/packs')
}

// ============== 右侧课程列表辅助 ==============
function fmtDur(sec) {
  sec = Math.max(0, sec | 0)
  const m = (sec / 60) | 0, s = sec % 60
  if (m >= 60) {
    const h = (m / 60) | 0
    return `${h}h${String(m % 60).padStart(2, '0')}m`
  }
  return `${m}m${String(s).padStart(2, '0')}s`
}
function stepText(step) {
  return ['待处理', '抽音频', 'ASR', '格式化', '完成'][step] || '未知'
}

// ============== 全局快捷键 ==============
function onKeyDown(e) {
  const tag = (e.target.tagName || '').toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return
  const v = videoRef.value
  ensureLoop()
  switch (e.key) {
    case ' ': case 'Spacebar':
      if (!v) return
      e.preventDefault(); v.paused ? v.play() : v.pause(); break
    case 'ArrowLeft':  e.preventDefault(); loop.gotoPrevSentence(); break
    case 'ArrowRight': e.preventDefault(); onClickNext(); break
    case 'r': case 'R': loop.seekSentence(state.si); break
    case 'l': case 'L': onToggleLock(); break
    case 'a': case 'A': onToggleAdaptive(); break
    case '[': {
      if (!v) return
      const r = Math.max(0.25, v.playbackRate - 0.1)
      loop.applyRate(r); toast('倍速 ' + r.toFixed(1) + '×'); break
    }
    case ']': {
      if (!v) return
      const r = Math.min(3, v.playbackRate + 0.1)
      loop.applyRate(r); toast('倍速 ' + r.toFixed(1) + '×'); break
    }
    case '1': loop.applySubtitleMode(0); break
    case '2': loop.applySubtitleMode(1); break
    case '3': loop.applySubtitleMode(2); break
    case '4': loop.applySubtitleMode(3); break
  }
}

// ============== 生命周期 ==============
let bindInterval = null
let oldV = null

onMounted(async () => {
  ensureLoop()
  syncPlaybackRateToPreset()

  const tryBind = () => {
    const v = videoRef.value
    if (v && v !== oldV) {
      if (oldV) {
        oldV.removeEventListener('seeking', loop.onSeeking)
        oldV.removeEventListener('timeupdate', loop.onTimeUpdate)
        oldV.removeEventListener('ended', onVideoEnded)
        oldV.removeEventListener('pause', onVideoPause)
      }
      v.addEventListener('seeking', loop.onSeeking)
      v.addEventListener('timeupdate', loop.onTimeUpdate)
      v.addEventListener('ended', onVideoEnded)
      v.addEventListener('pause', onVideoPause)
      oldV = v
    }
  }
  tryBind()
  bindInterval = setInterval(tryBind, 500)

  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('mousedown', onGlobalClick)
  // 每 5 秒保存播放进度
  progressTimer = setInterval(() => saveProgress(false), 5000)
  window.addEventListener('beforeunload', onBeforeUnloadProgress)
  await loadLesson(route.params.id)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown)
  window.removeEventListener('mousedown', onGlobalClick)
  window.removeEventListener('beforeunload', onBeforeUnloadProgress)
  if (progressTimer) clearInterval(progressTimer)
  // 退出前保存播放进度
  saveProgress(false)
  if (bindInterval) clearInterval(bindInterval)
  const v = videoRef.value
  if (v && loop) {
    v.removeEventListener('seeking', loop.onSeeking)
    v.removeEventListener('timeupdate', loop.onTimeUpdate)
    v.removeEventListener('ended', onVideoEnded)
    v.removeEventListener('pause', onVideoPause)
  }
})

// 路由参数变化时重新加载（同组件复用情况）
watch(() => route.params.id, (newId) => {
  if (newId) loadLesson(newId)
})
</script>

<style scoped>
.player-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}
.app-main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 12px;
  min-height: 0;
  flex: 1;
}
@media (max-width: 960px) {
  .app-main { grid-template-columns: 1fr; }
}

/* 左侧：视频 + 句子列表 垂直堆叠 */
.left-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* 句子列表：视频下方，固定约 5 句高度，可滚动；
   当前句变化时 SentenceList 内部自动滚动让该句居中（效果就是"向上滑一句"） */
.sen-panel-wrap {
  height: 380px;
  min-height: 260px;
  max-height: 60vh;
}
.sen-panel-wrap :deep(.sen-panel) {
  height: 100%;
}

/* 右侧：同包课程列表 */
.lessons-col {
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--panel);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.col-head {
  padding: 12px 14px;
  border-bottom: 1px solid var(--line);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.col-head b { font-size: 14px; }
.pack-lessons-list {
  flex: 1;
  overflow-y: auto;
  padding: 6px;
}
.empty-hint {
  padding: 24px 12px;
  text-align: center;
  color: var(--mute);
  font-size: 13px;
}
.pack-lesson-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all .12s ease;
  margin: 2px 0;
}
.pack-lesson-item:hover:not(.disabled) {
  background: #1a2550;
  border-color: var(--line);
}
.pack-lesson-item.current {
  background: linear-gradient(135deg, #243980, #462a83);
  border-color: var(--accent);
  box-shadow: 0 4px 16px #6ea8ff22;
}
.pack-lesson-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pli-idx {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #0d1636;
  color: var(--mute);
  font-size: 12px;
  font-weight: 600;
}
.pack-lesson-item.current .pli-idx {
  background: var(--accent);
  color: #fff;
}
.playing-mark { font-size: 11px; }
.pli-main { flex: 1; min-width: 0; }
.pli-title {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pack-lesson-item.current .pli-title { color: #fff; }
.pli-meta {
  display: flex;
  gap: 5px;
  align-items: center;
  color: var(--mute);
  font-size: 11px;
  margin-top: 3px;
}
.proc-tag {
  background: #4a3a0e;
  color: var(--warn);
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
}

.load-error {
  margin-top: 16px;
  padding: 16px;
  border: 1px solid var(--bad);
  border-radius: 10px;
  background: #2a0f1f55;
  color: var(--bad);
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>
